import { getRuntimeEnv } from "@/lib/runtimeEnv";

const normalizeHost = (host: string) => host.split(":")[0].toLowerCase();
const DEFAULT_TENANT_ROOT_DOMAIN = "hub.wyniai.com";
const LOCAL_TENANT_ROOT_DOMAIN = "localhost";

const splitRootDomains = (value?: string): string[] =>
  (value || "")
    .split(",")
    .map((root) => normalizeHost(root.trim()))
    .filter(Boolean);

const resolveRootDomains = (hostname: string): string[] => {
  const roots = new Set<string>();
  const envRoot = getRuntimeEnv("VITE_TENANT_ROOT_DOMAIN");
  for (const root of splitRootDomains(envRoot)) {
    roots.add(root);
  }

  const normalized = normalizeHost(hostname);
  if (normalized === "localhost" || normalized === "127.0.0.1" || normalized.endsWith(".localhost")) {
    roots.add(LOCAL_TENANT_ROOT_DOMAIN);
  }

  roots.add(DEFAULT_TENANT_ROOT_DOMAIN);

  return Array.from(roots).sort((a, b) => b.length - a.length);
};

export const getTenantRootDomain = (hostname: string): string | null => {
  const normalized = normalizeHost(hostname);
  for (const rootDomain of resolveRootDomains(normalized)) {
    if (normalized === rootDomain || normalized.endsWith(`.${rootDomain}`)) {
      return rootDomain;
    }
  }
  return resolveRootDomains(normalized)[0] || null;
};

export const isTenantRootDomain = (hostname: string): boolean => {
  const normalized = normalizeHost(hostname);
  return resolveRootDomains(normalized).some((rootDomain) => normalized === rootDomain);
};

export const getTenantSubdomain = (hostname: string): string | null => {
  const normalized = normalizeHost(hostname);
  const rootDomains = resolveRootDomains(normalized);

  for (const rootDomain of rootDomains) {
    if (normalized === rootDomain) {
      return null;
    }

    if (normalized.endsWith(`.${rootDomain}`)) {
      const prefix = normalized.slice(0, -(rootDomain.length + 1));
      if (!prefix) {
        return null;
      }
      return prefix.split(".")[0] || null;
    }
  }

  return null;
};
